import * as THREE from 'three';

import type { ArtificialFillRef } from './artificialFill';
import type { CoverageFillRef } from './coverageFill';
import { loadFlippedBitmap } from './imageBitmap.ts';
import { SOL_PHOTOSPHERE_PARS, type SolPhotosphereRef } from './solPhotosphere.ts';
import { SUN_LIMB_U1, SUN_LIMB_U2 } from './sunCover.ts';
import { trackLightKeyed } from '../world/scene.ts';

/** IAU nominal solar radius, kilometres. */
export const SOLAR_RADIUS_KM = 695700;

/**
 * The photosphere's radiance, W/m2/sr: sigma T^4 / pi at the IAU nominal 5772 K.
 *
 * The same at every distance, since the irradiance and the disc's solid angle
 * both fall as 1/d^2, and five orders over any sky: drawn in the sky's own
 * units it is past the top of the tone curve, which is why the sun from the
 * ground is white to its limb.
 */
export const SOLAR_RADIANCE_WM2SR = (5.670374419e-8 * 5772 ** 4) / Math.PI;

/**
 * Radiance the globe map's own white draws at.
 *
 * The map is a DISPLAY product - `tools/atlas/bake_sol_map.py` writes AIA's
 * 304 A DN/s through a white ramp - so the number that
 * matters is not a physical ratio but where its range lands on the tone curve.
 * The renderer is ACES (`src/main.ts`) at the Atlas's band exposure, 1.25 above
 * the streaming edge (`src/atlas.ts`, `BAND_EXPOSURE`), and three's fit is
 * `RRTAndODTFit(colour * exposure / 0.6)`: at a radiance of 1.6, where the disc
 * used to sit, the curve returns 0.96 of white with a local slope near 0.02, so
 * everything the map held arrived as one flat bright tone. This is set instead
 * by where the table's own WHITE should land: at 0.6 it reaches 238 of 255 at
 * the band exposure above the streaming edge, the top of the range with nothing
 * clipped, and the rest of the table spreads down the curve under it.
 * `descent-dojo --coverage --body sol` reads that spread, its chroma and its
 * headroom, and `--inject hot-exposure` puts the pre-fix draw back.
 */
const MAP_EXPOSURE = 1.1;

/**
 * The pre-fix draw the exposure control restores.
 *
 * What the disc did while its map was HMI continuum: the bake's own
 * `INTENSITY_SCALE` multiplied back, then the flat disc's radiance on top of
 * that. Kept only so `setDebug({ hotExposure: true })` can restore it exactly.
 */
const HOT_INTENSITY_SCALE = 1.4;

/**
 * How far out the glare reaches, in solar radii.
 *
 * A fixed multiple of the disc, so the aureole is a fixed multiple of Sol's
 * angular diameter at whatever distance the camera is at and never a size in
 * pixels that a resolution change would move.
 */
const GLARE_SPAN = 6;

/**
 * How far past its rim the disc's quad reaches from the ground, in disc radii.
 *
 * The space view ramps its antialiasing INSIDE the rim, which on a disc a few
 * pixels across takes half a pixel off its radius - a fifth of the sun from
 * Mars. From the ground the ramp is centred on the rim instead, and the half
 * pixel outside it needs geometry to land on.
 */
const GROUND_QUAD_SPAN = 1.5;

/**
 * Apparent diameter Sol has to reach before the glare is worth a pass.
 *
 * The same rule and the same number `atmoSky.ts` applies to the scattering
 * shell: 0.05 degrees is one pixel at the reference viewport, and an aureole
 * around a disc that small is under a pixel of its own. Read off the TRUE
 * angular diameter and not the drawn one, because the drawn one is floored to
 * keep the disc clickable (`orrery.ts`'s `MIN_DRAWN_ANGULAR_DEG`) and would
 * never fall through this gate.
 *
 * Which is also why the gate cannot be a toggle. What is DRAWN at the moment
 * the true diameter crosses this is a disc held at the 0.15 degree floor with
 * an aureole six of its radii across - 0.9 degrees, 18 px at the reference
 * viewport - so switching it off in one frame is a visible pop of exactly the
 * kind the descent recorder counts. The glare fades to nothing over the decade
 * of true angular size above the gate instead.
 */
const GLARE_MIN_ANGULAR_DIAMETER_DEG = 0.05;
/** Where the fade above the gate is done: one decade of true angular size. */
const GLARE_FULL_ANGULAR_DIAMETER_DEG = 0.5;

/** 1 above the fade's top, 0 at the gate, log-linear between the two. */
export function subPixelFade(trueDeg: number, gateDeg: number, fullDeg: number): number {
  if (trueDeg <= gateDeg) return 0;
  if (trueDeg >= fullDeg) return 1;
  return Math.log10(trueDeg / gateDeg) / Math.log10(fullDeg / gateDeg);
}

export interface SolOptions {
  /** Scene units per kilometre. Default 1000: the world model is in metres. */
  unitsPerKm?: number;
  /**
   * Floor on the drawn apparent diameter, degrees. Below this the disc is drawn
   * larger than it is, because a disc a fifth of a pixel across is not drawn at
   * all. The default is under Sol's real 0.0135 deg from Pluto, so every body in
   * the Phase 1 roster sees the true angular size.
   */
  minAngularDiameterDeg?: number;
  /** Peak radiance at disc centre of the BARE disc, linear. */
  intensity?: number;
  /** Radiance the globe map's own white draws at; see `MAP_EXPOSURE`. */
  mapExposure?: number;
  /**
   * Peak radiance of the glare, linear, at the disc's CENTRE - the falloff is
   * 1/(1 + u^3) in disc radii, so the disc's own rim reads half of it. Default
   * is half the disc's, which puts the aureole at the rim at a quarter of the
   * disc, where the one around a real solar disc sits.
   */
  glareIntensity?: number;
  /** Segments around the rim. */
  segments?: number;
  /**
   * The registry row's `texture`: one Carrington rotation of AIA 304 A
   * brightness, in white, as a 2:1 map, or null for the bare disc.
   */
  texture?: string | null;
  /** The row's `albedo_color`, the one texel drawn while the map is in flight. */
  albedoColor?: string;
  /** The coverage-fill uniforms, so the unmeasured polar caps can be forced to
   *  another colour by an instrument the way every other body's holes are. */
  coverage?: CoverageFillRef;
  /**
   * The artificial-fill uniforms, for the same layer every other body's holes
   * answer to. The disc is not a globe, so `artificialFill.ts`'s chunk cannot
   * be spliced into a standard material here and the mix is written into this
   * module's own shader instead - on the same alpha rule and in the same place
   * in the order: after the map is read and before coverage paints.
   */
  artificial?: ArtificialFillRef | null;
  /**
   * The live photosphere's uniforms (`solPhotosphere.ts`).
   *
   * Left out, the disc is what it was: the 304 A map or the flat tint under
   * the bare limb law, with `uSunLive` held at 0 and the region loop never
   * entered.
   */
  photosphere?: SolPhotosphereRef | null;
}

export interface SolState {
  /** Heliocentric distance of the camera at the last update, kilometres. */
  distanceKm: number;
  /** True apparent diameter at that distance, degrees. */
  angularDiameterDeg: number;
  /** What was actually drawn, degrees; differs only where the floor engages. */
  drawnAngularDiameterDeg: number;
}

export interface SolHandle {
  mesh: THREE.Mesh;
  /** The aureole, a child of `mesh`: hidden on its own when Sol is sub-pixel. */
  glare: THREE.Mesh;
  state: SolState;
  /**
   * Sol's body-fixed rotation, from the caller's own `bodyFixedQuaternion`.
   *
   * The disc is billboarded, so which part of the photosphere faces the camera
   * is not in its own transform; this is what puts the map's Carrington
   * longitude under the camera and turns it at W_rate.
   */
  setBodyQuaternion(q: THREE.Quaternion): void;
  /**
   * The same rotation read back.
   *
   * Sol is in no `byId` map, so this is what lets a caller put a lat/lon on it
   * in the world the way `surfaceWorld` does for a body the orrery holds.
   */
  bodyQuaternion(): THREE.Quaternion;
  /**
   * The disc-local to body-fixed rotation the shader reads, as it stands.
   *
   * Its transpose takes a body-fixed direction back to the disc's own (x, y,
   * mu), which is where on the billboard a heliographic point is drawn - the
   * one way a check can ask where a spot ought to be without rebuilding the
   * shader's geometry beside it.
   */
  toBodyMatrix(): THREE.Matrix3;
  /**
   * The two states the coverage arm's globe-map reading has to fail under.
   *
   * `glareOverDisc` sums the aureole over the disc again, as it was before it
   * was held off it: the one state in which the photosphere clips to white at
   * every pixel. `hotExposure` puts the pre-fix material back - the bake's own
   * intensity scale, the star tint and the limb law over the map - which is
   * what drove every texel into the tone curve's shoulder and left the disc a
   * flat bright circle.
   */
  setDebug(d: { glareOverDisc?: boolean; hotExposure?: boolean }): void;
  /**
   * The sun seen from a body's ground, or from space (null).
   *
   * From the ground the disc is the bare white photosphere at `radiance`, in
   * the renderer's own units, under the limb law and in the hue of what the
   * air leaves of it (`transmittance`, white where none is given): no 304 A map,
   * which is a chromospheric display product for the close-up view, and no live layer
   * on a disc a few pixels across. The glare is kept but drawn as a MAX
   * rather than a sum: on a black sky that is the same thing, and on a lit
   * one it no longer lifts the sky round the disc to white, which would draw
   * the sun as a white blob several times its own size. The glare carries the
   * transmittance too, so a low sun's aureole is its colour and not white.
   */
  setGroundView(radiance: number | null, transmittance?: THREE.Vector3): void;
  /** The share of the disc's light no body in front of it takes (`sunCover.ts`); the aureole carries it. */
  setVisibleShare(share: number): void;
  update(cameraHeliocentricKm: { x: number; y: number; z: number }): void;
  dispose(): void;
}

const solVertexShader = /* glsl */ `
  varying vec2 vLocal;

  #include <common>
  #include <logdepthbuf_pars_vertex>

  void main() {
    vLocal = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    #include <logdepthbuf_vertex>
  }
`;

/**
 * Limb darkening is the standard quadratic law I(mu)/I(0) = 1 - u1 (1 - mu)
 * - u2 (1 - mu)^2 with mu = cos of the angle from disc centre. No published
 * table gives exactly u1 = 0.47, u2 = 0.23; the nearest is Claret (2000,
 * A&A 363, 1081), ATLAS quadratic grid, Teff 5750 K / log g 4.50 (nearest
 * grid point to the Sun's 5777 K / 4.44), Johnson V, VT 2 km/s: (0.437,
 * 0.295) - off by +0.033 and -0.065 from ours.
 *
 * From space the disc is drawn as the white photosphere it is, so the law is
 * over the map as well as the bare disc. The map keeps 304 A's brightness and
 * drops its false colour at bake time; every texel was measured within 8
 * degrees of a central meridian, so the limb it carries is the law's and not
 * the channel's (at 304 A the limb brightens). The disc stays a billboard
 * to carry the map: a point at radius r on the unit disc is the point
 * (x, y, mu) of the unit sphere facing the camera, so `uToBody` - the disc's
 * own rotation composed with Sol's body-fixed frame - is all it takes to read
 * a Carrington latitude and longitude out of a screen-space quad.
 *
 * `uTint` is white. An atmosphere's tint on the Sun belongs to a view from
 * under it, never to the disc seen from space, so only the ground view's
 * `uGroundT` carries one (`setGroundView`).
 *
 * The live photosphere (`solPhotosphere.ts`) rides ON that map. It drew on the
 * bare tinted disc instead until the frame leg measured what a player saw: the
 * bare disc's own radiance is 1.6, where the tone curve's slope is near 0.02,
 * so every umbra, facula and granule the layer painted arrived as a flat white
 * circle. The map is the base with slope under it, which is why the card names
 * both: the 304 A frame is the measurement and the regions over it are the
 * model, and the reading's note says so on every date.
 */
const solFragmentShader = /* glsl */ `
  precision highp float;
  varying vec2 vLocal;
  uniform vec3 uTint;
  uniform float uIntensity;
  uniform sampler2D uMap;
  uniform float uHasMap;
  uniform mat3 uToBody;
  uniform vec3 fxUnmeasured;
  uniform float fxUnmeasuredOn;
  uniform float uMapExposure;
  uniform float uHot;
  uniform sampler2D fxArtMap;
  uniform float fxArtOn;
  uniform float fxArtAll;
  uniform float uGround;
  uniform float uGroundRadiance;
  uniform vec3 uGroundT;
  uniform float uEdge;

  #include <common>
  #include <logdepthbuf_pars_fragment>
${SOL_PHOTOSPHERE_PARS}

  void main() {
    float r = length(vLocal) / uEdge;
    float w = max(fwidth(r), 1e-5);
    float alpha = uGround > 0.5
      ? clamp(0.5 + (1.0 - r) / w, 0.0, 1.0)
      : 1.0 - smoothstep(1.0 - w, 1.0, r);
    if (alpha <= 0.0) discard;
    #include <logdepthbuf_fragment>

    float mu = sqrt(max(0.0, 1.0 - min(1.0, r * r)));
    float limb = max(0.0, 1.0 - ${SUN_LIMB_U1.toFixed(2)} * (1.0 - mu) - ${SUN_LIMB_U2.toFixed(2)} * (1.0 - mu) * (1.0 - mu));
    vec3 face = uTint * uIntensity * limb;
    // Body-fixed axes are the ones every lat/lon in this repo is written in:
    // x at longitude 0, y north, longitude east toward -z.
    vec3 p = normalize(uToBody * vec3(vLocal, mu));
    // uSunBare alone would drop the map with the live layer off too. The old
    // bug held the map off only while the live layer was on; match that.
    if (uHasMap > 0.5 && !(uSunBare > 0.5 && uSunLive > 0.5)) {
      float lat = asin(clamp(p.y, -1.0, 1.0));
      float lon = atan(-p.z, p.x);
      vec2 uv = vec2(lon / (2.0 * PI) + 0.5, lat / PI + 0.5);
      vec4 texel = texture2D(uMap, uv);
      // artificialFill.ts's mix, on the disc's own sampler: weighted by the
      // FILL's alpha, so a measured texel is the identity whatever the layer
      // says, and fxArtAll is the control that drops that alpha out.
      vec4 fxArt = texture2D(fxArtMap, uv);
      vec3 photosphere = mix(texel.rgb, fxArt.rgb, fxArtOn * max(fxArt.a, fxArtAll));
      // The same substitution coverageFill.ts makes on every other body, and
      // after the fill for the same reason it is after it there: coverage
      // paints what is still unfilled.
      photosphere = mix(photosphere, fxUnmeasured, fxUnmeasuredOn * (1.0 - texel.a));
      vec3 hot = uTint * photosphere * ${HOT_INTENSITY_SCALE.toFixed(1)} * uIntensity * limb;
      face = mix(photosphere * uMapExposure * limb, hot, uHot);
    }
    if (uSunLive > 0.5) {
      // One pixel is w of arc across the disc and w/mu of it along the radius,
      // so one isotropic filter width has to sit between the two: their
      // geometric mean. The radial one alone smoothed a small group near the
      // limb over more than its own umbra, and near the limb is where every
      // group sits in the view opening Sol gives. w itself is one pixel in the
      // disc's own radii, which is what the smallest drawn group is floored on.
      face = solPhotosphere(face, p, mu, max(w / sqrt(max(mu, 0.05)), 1e-4), w);
    }
    // From the ground the disc is thousands of times over the tone curve's
    // shoulder, and a tint taken through the curve clips back to white. The
    // curve sets the level off the brightest channel and the hue goes on
    // after it; a dimmed level lets the sky through rather than going dark.
    vec3 chroma = vec3(1.0);
    if (uGround > 0.5) {
      vec3 c = uTint * uGroundT;
      float peak = max(max(c.r, c.g), c.b);
      face = vec3(uGroundRadiance * limb * peak);
      chroma = peak > 0.0 ? c / peak : vec3(0.0);
    }
    gl_FragColor = vec4(face, alpha);
    // The same two chunks the glare and the sky quad carry. Without them the
    // disc wrote a raw linear radiance straight to an sRGB framebuffer, so it
    // was the one thing in the Atlas that neither the tone curve nor the
    // per-band exposure reached.
    #include <tonemapping_fragment>
    if (uGround > 0.5) gl_FragColor = vec4(chroma, alpha * min(gl_FragColor.g, 1.0));
    #include <colorspace_fragment>
  }
`;

/**
 * The aureole, as an inverse-cube falloff in solar radii.
 *
 * 1/(1 + u^3) is the shape a scattered-light aureole around a bright disc
 * actually has - half the peak at the limb and two orders down six radii out -
 * and it is written in the disc's OWN radii, so what the glare covers is a
 * fixed multiple of Sol's angular diameter at every distance.
 */
const glareFragmentShader = /* glsl */ `
  precision highp float;
  varying vec2 vLocal;
  uniform vec3 uTint;
  uniform float uIntensity;
  uniform float uSpan;
  uniform float uOverDisc;

  #include <common>
  #include <logdepthbuf_pars_fragment>

  void main() {
    float r = length(vLocal);
    if (r >= 1.0) discard;
    #include <logdepthbuf_fragment>

    float u = r * uSpan;
    float g = uIntensity / (1.0 + u * u * u);
    // The aureole is scattered light AROUND the disc, and adding it over the
    // disc as well put a second exposure on every photosphere pixel: the two
    // together clipped the whole face to white, which is why neither the limb
    // darkening nor the map under it could be seen. u is the radius in the
    // disc's OWN radii, so this is the complement of the disc's edge coverage
    // and the two meet across the antialiased limb with no seam either way.
    g *= mix(smoothstep(1.0 - max(fwidth(u), 1e-5), 1.0, u), 1.0, uOverDisc);
    // Taken to zero before the quad's own edge, or the falloff would end on a
    // visible rim of the geometry rather than on nothing.
    g *= 1.0 - smoothstep(0.8, 1.0, r);
    gl_FragColor = vec4(uTint * g, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

/** One opaque sRGB texel of a `#rrggbb`, for the stand-in map. */
function solidTexel(cssColor: string): Uint8Array<ArrayBuffer> {
  const hex = new THREE.Color(cssColor).getHex(THREE.SRGBColorSpace);
  const out = new Uint8Array(new ArrayBuffer(4));
  out.set([(hex >> 16) & 255, (hex >> 8) & 255, hex & 255, 255]);
  return out;
}

/**
 * Sol as a star: an emissive limb-darkened disc at the angular diameter its
 * heliocentric distance implies, rather than a lit ball with a terminator.
 *
 * `update` takes the CAMERA's heliocentric position and places the disc at the
 * Sun's position relative to it, which assumes camera-relative rendering with
 * the camera at the scene origin (`src/world/frame.ts`). Radius is physical, so
 * depth against the planets is real; the disc is billboarded because a sphere
 * that never shows a terminator is a disc with more triangles - and the globe
 * map rides on it anyway, reconstructed per fragment out of the disc's own
 * radius rather than out of a mesh's UVs.
 */
export function createSol(opts: SolOptions = {}): SolHandle {
  const unitsPerKm = opts.unitsPerKm ?? 1000;
  const minDiamDeg = opts.minAngularDiameterDeg ?? 0.012;
  const segments = opts.segments ?? 96;

  const spaceTint = new THREE.Color(1, 1, 1);
  const geo = new THREE.CircleGeometry(1, segments);
  // A sampler with no image reads three's empty texture, which is four zero
  // bytes, so the disc would go black for as long as the map is in flight and
  // for ever if the request failed. One texel of the row's own albedo_color
  // stands in and `uHasMap` keeps it out of the shader's own path until the
  // image lands - the same pair the orrery's globes use, for the same reason
  // that three allocates immutable storage at the first upload.
  const flat = new THREE.DataTexture(solidTexel(opts.albedoColor ?? '#ffffff'), 1, 1);
  flat.name = 'sol-flat';
  flat.colorSpace = THREE.SRGBColorSpace;
  flat.needsUpdate = true;
  const map = new THREE.Texture();
  map.name = 'sol-map';
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 8;
  map.flipY = false;
  const coverage: CoverageFillRef = opts.coverage
    ?? { unmeasured: { value: new THREE.Color(opts.albedoColor ?? '#ffffff') },
      on: { value: 0 } };
  // No fill published for this body is the same three uniforms held at nothing,
  // which is the mix's own identity - rather than a second shader without it.
  const artificial: ArtificialFillRef = opts.artificial
    ?? { map: { value: null }, on: { value: 0 }, force: { value: 0 }, dn: { value: new THREE.Vector3() } };
  // The shader declares the live layer's samplers whether or not one is wired,
  // so a disc built without a photosphere carries one dead texel rather than a
  // second shader without the branch. It is this module's, so it frees it.
  const noRegions = opts.photosphere
    ? null
    : new THREE.DataTexture(new Float32Array(4), 1, 1, THREE.RGBAFormat, THREE.FloatType);
  if (noRegions !== null) {
    noRegions.name = 'sol-regions-none';
    noRegions.needsUpdate = true;
  }
  const photosphere: SolPhotosphereRef = opts.photosphere ?? {
    regions: { value: noRegions! },
    count: { value: 0 },
    dt: { value: 0 },
    rot: { value: new THREE.Vector3() },
    lonOffset: { value: 0 },
    live: { value: 0 },
    granule: { value: new THREE.Vector4(0, 1, 0, 0) },
    granuleScale: { value: 1 },
    bare: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uTint: { value: spaceTint.clone() },
      uIntensity: { value: opts.intensity ?? 1.6 },
      uMap: { value: flat },
      uHasMap: { value: 0 },
      uToBody: { value: new THREE.Matrix3() },
      fxUnmeasured: coverage.unmeasured,
      fxUnmeasuredOn: coverage.on,
      uMapExposure: { value: opts.mapExposure ?? MAP_EXPOSURE },
      uHot: { value: 0 },
      fxArtMap: artificial.map,
      fxArtOn: artificial.on,
      fxArtAll: artificial.force,
      uSunRegions: photosphere.regions,
      uSunCount: photosphere.count,
      uSunDt: photosphere.dt,
      uSunRot: photosphere.rot,
      uSunLonOffset: photosphere.lonOffset,
      uSunLive: photosphere.live,
      uSunGranule: photosphere.granule,
      uSunGranuleScale: photosphere.granuleScale,
      uSunBare: photosphere.bare,
      uGround: { value: 0 },
      uGroundRadiance: { value: 0 },
      uGroundT: { value: new THREE.Vector3(1, 1, 1) },
      uEdge: { value: 1 },
    },
    vertexShader: solVertexShader,
    fragmentShader: solFragmentShader,
    transparent: true,
    depthWrite: true,
    blending: THREE.NormalBlending,
    side: THREE.FrontSide,
  });

  mat.name = 'sol-disc';

  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'sol';
  mesh.userData['bodyId'] = 'sol';
  mesh.frustumCulled = false;

  // A child of the disc, so the billboarding, the position and the scale are
  // the disc's own and the orrery's one `sol.mesh.visible` still hides both.
  // Unit radius and a scale, not a radius of GLARE_SPAN: the vertex shader
  // hands `position.xy` straight to the fragment as the falloff's own
  // coordinate, so the geometry has to stay normalised.
  // Held as its own value, not only as a uniform: the sub-pixel fade scales it
  // every frame and needs the unfaded one to scale.
  const glareIntensity = opts.glareIntensity ?? (opts.intensity ?? 1.6) * 0.5;
  const glareGeo = new THREE.CircleGeometry(1, segments);
  const glareMat = new THREE.ShaderMaterial({
    uniforms: {
      uTint: { value: spaceTint.clone() },
      uIntensity: { value: glareIntensity },
      uSpan: { value: GLARE_SPAN },
      uOverDisc: { value: 0 },
    },
    vertexShader: solVertexShader,
    fragmentShader: glareFragmentShader,
    transparent: true,
    // No depth write: the aureole is light scattered in front of whatever is
    // behind it, not a surface, and it is coplanar with the disc.
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.FrontSide,
  });
  glareMat.name = 'sol-glare';
  const glare = new THREE.Mesh(glareGeo, glareMat);
  glare.name = 'sol-glare';
  glare.frustumCulled = false;
  glare.scale.setScalar(GLARE_SPAN);
  mesh.add(glare);

  const state: SolState = { distanceKm: 0, angularDiameterDeg: 0, drawnAngularDiameterDeg: 0 };
  const minHalfAngle = (minDiamDeg * 0.5 * Math.PI) / 180;

  let disposed = false;
  let bitmap: ImageBitmap | null = null;
  if (opts.texture) {
    void loadFlippedBitmap(opts.texture).then((b) => {
      if (disposed) { b.close(); return; }
      bitmap = b;
      map.image = b;
      map.needsUpdate = true;
      mat.uniforms['uMap']!.value = map;
      mat.uniforms['uHasMap']!.value = 1;
      flat.dispose();
    }).catch((err: unknown) => {
      console.warn('[atlas] sol\'s globe map did not load; the disc is its tint:', err);
    });
  }

  // Scene axes to body-fixed, inverted each frame into the disc's own local
  // axes. Identity until the caller hands one over, which draws the map on
  // scene axes rather than not at all - the same thing the orrery's globes do
  // for a body with no published pole.
  const bodyQ = new THREE.Quaternion();
  const _toBody = new THREE.Quaternion();
  const _basis = new THREE.Matrix4();
  const setBodyQuaternion = (q: THREE.Quaternion): void => { bodyQ.copy(q); };
  const bodyQuaternion = (): THREE.Quaternion => bodyQ;

  const setDebug = (d: { glareOverDisc?: boolean; hotExposure?: boolean }): void => {
    if (d.glareOverDisc !== undefined) {
      glareMat.uniforms['uOverDisc']!.value = d.glareOverDisc ? 1 : 0;
    }
    if (d.hotExposure !== undefined) {
      mat.uniforms['uHot']!.value = d.hotExposure ? 1 : 0;
    }
  };

  let ground = false;
  /** The quad's radius over the disc's: `GROUND_QUAD_SPAN` from the ground, 1 from space. */
  let quadSpan = 1;
  const setGroundView = (radiance: number | null, transmittance?: THREE.Vector3): void => {
    if (radiance !== null) {
      mat.uniforms['uGroundRadiance']!.value = radiance;
      const t = mat.uniforms['uGroundT']!.value as THREE.Vector3;
      if (transmittance) t.copy(transmittance);
      else t.set(1, 1, 1);
      (glareMat.uniforms['uTint']!.value as THREE.Color).setRGB(t.x, t.y, t.z);
    }
    if ((radiance !== null) === ground) return;
    ground = radiance !== null;
    const span = ground ? GROUND_QUAD_SPAN : 1;
    mesh.scale.multiplyScalar(span / quadSpan);
    quadSpan = span;
    glare.scale.setScalar(GLARE_SPAN / span);
    mat.uniforms['uEdge']!.value = 1 / span;
    mat.uniforms['uGround']!.value = ground ? 1 : 0;
    if (!ground) (glareMat.uniforms['uTint']!.value as THREE.Color).copy(spaceTint);
    glareMat.blending = ground ? THREE.CustomBlending : THREE.AdditiveBlending;
    glareMat.blendEquation = ground ? THREE.MaxEquation : THREE.AddEquation;
  };

  let visibleShare = 1;
  let lastGlareFade = 1;
  const setVisibleShare = (share: number): void => {
    if (share === visibleShare) return;
    visibleShare = share;
    glareMat.uniforms['uIntensity']!.value = glareIntensity * lastGlareFade * visibleShare;
  };

  const update = (cameraHeliocentricKm: { x: number; y: number; z: number }): void => {
    const cx = cameraHeliocentricKm.x;
    const cy = cameraHeliocentricKm.y;
    const cz = cameraHeliocentricKm.z;
    const d = Math.hypot(cx, cy, cz);
    state.distanceKm = d;

    if (!(d > SOLAR_RADIUS_KM)) {
      // Inside the photosphere there is no disc to draw and no direction to it.
      mesh.visible = false;
      glare.visible = false;
      state.angularDiameterDeg = 180;
      state.drawnAngularDiameterDeg = 180;
      return;
    }
    mesh.visible = true;

    const halfAngle = Math.asin(SOLAR_RADIUS_KM / d);
    state.angularDiameterDeg = (2 * halfAngle * 180) / Math.PI;
    const drawnHalf = Math.max(halfAngle, minHalfAngle);
    state.drawnAngularDiameterDeg = (2 * drawnHalf * 180) / Math.PI;

    const glareFade = subPixelFade(
      state.angularDiameterDeg, GLARE_MIN_ANGULAR_DIAMETER_DEG,
      GLARE_FULL_ANGULAR_DIAMETER_DEG);
    glare.visible = glareFade > 0;
    lastGlareFade = glareFade;
    glareMat.uniforms['uIntensity']!.value = glareIntensity * glareFade * visibleShare;

    const radiusKm = drawnHalf === halfAngle ? SOLAR_RADIUS_KM : d * Math.tan(drawnHalf);
    mesh.position.set(-cx * unitsPerKm, -cy * unitsPerKm, -cz * unitsPerKm);
    mesh.scale.setScalar(radiusKm * unitsPerKm * quadSpan);
    mesh.lookAt(0, 0, 0);
    mesh.updateMatrixWorld();
    // After `lookAt`, because the billboard's own rotation is half of this
    // product: a disc-local direction goes to scene axes through the mesh's
    // quaternion and from there to body-fixed through the inverse of Sol's.
    _toBody.copy(bodyQ).invert().multiply(mesh.quaternion);
    (mat.uniforms['uToBody']!.value as THREE.Matrix3)
      .setFromMatrix4(_basis.makeRotationFromQuaternion(_toBody));
  };

  // Neither shader reads a light and neither relinks itself, so both are
  // rekeyed with the rest of the scene's unlit materials when a light moves.
  // `RawShaderMaterial` is the one kind three leaves out of that key and it is
  // not open here: the log depth, tone mapping and colour space below come from
  // three's own chunks, whose defines exist only for a non-raw material.
  const untrackMat = trackLightKeyed(mat);
  const untrackGlare = trackLightKeyed(glareMat);

  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    untrackMat();
    untrackGlare();
    mesh.removeFromParent();
    glare.removeFromParent();
    geo.dispose();
    mat.dispose();
    glareGeo.dispose();
    glareMat.dispose();
    flat.dispose();
    map.dispose();
    noRegions?.dispose();
    bitmap?.close();
  };

  const toBodyMatrix = (): THREE.Matrix3 => mat.uniforms['uToBody']!.value as THREE.Matrix3;

  return {
    mesh, glare, state, setBodyQuaternion, bodyQuaternion, toBodyMatrix, setDebug, setGroundView,
    setVisibleShare, update, dispose,
  };
}
